/**
 * Gelaendedaten auf dem Geraet.
 *
 * Der Server haelt die grossen Kacheln und gibt nur den ausgeduennten
 * Ausschnitt um das Objekt heraus. Der wird hier abgelegt — zusammen mit den
 * Kartenkacheln vorgeladen und danach ohne Verbindung verwendbar. Gerechnet
 * wird auf dem Geraet, wie alles andere auch.
 */

import {
  bezugspunkt,
  findeVerschattungsobjekte,
  nachGeo,
  nachGrundriss,
  objekthoehen,
  type Geo,
  type GefundenesObjekt,
  type Hoehenraster,
} from '@pv/geometrie';

import { db } from './db.ts';

/** Wie der Server das Fenster herausgibt. */
export interface Hoehenfenster {
  ursprung: Geo;
  spalten: number;
  zeilen: number;
  zellgroesseM: number;
  werte: Array<number | null>;
}

export interface Gelaendedaten {
  projekt_id: string;
  dgm: Hoehenfenster | null;
  dom: Hoehenfenster | null;
  umkreisM: number;
  geholt_am: string;
}

/** Beim Vorbereiten mitladen — danach liegt es lokal. */
export async function holeGelaendedaten(
  projektId: string,
  lat: number,
  lng: number,
  umkreisM: number,
  token: string | null,
): Promise<{ ok: boolean; meldung: string }> {
  const antwort = await fetch(
    `/api/hoehendaten?lat=${lat}&lng=${lng}&umkreis=${Math.round(umkreisM)}&weite=2`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );

  if (antwort.status === 404) {
    return {
      ok: false,
      meldung: 'Für diesen Standort liegen keine Geländedaten am Server.',
    };
  }
  if (!antwort.ok) return { ok: false, meldung: `Geländedaten: Fehler ${antwort.status}` };

  const daten = (await antwort.json()) as {
    dgm: Hoehenfenster | null;
    dom: Hoehenfenster | null;
    umkreisM: number;
  };
  await db.hoehendaten.put({
    projekt_id: projektId,
    dgm: daten.dgm,
    dom: daten.dom,
    umkreisM: daten.umkreisM,
    geholt_am: new Date().toISOString(),
  });

  const fehlt = !daten.dgm ? 'Geländemodell' : !daten.dom ? 'Oberflächenmodell' : null;
  return {
    ok: !fehlt,
    meldung: fehlt
      ? `Nur ein Teil vorhanden — das ${fehlt} fehlt. Ohne beide keine Objekterkennung.`
      : 'Geländedaten geladen.',
  };
}

/**
 * Ein Fenster vom Server in ein Raster im Grundriss des Projekts umrechnen.
 *
 * Eine Ungenauigkeit, die man kennen sollte: Das Raster ist an den Achsen der
 * Quellprojektion ausgerichtet, der Grundriss an der echten Nordrichtung. In
 * Oesterreich liegen die um bis zu rund anderthalb Grad auseinander. Auf einem
 * zwanzig Meter langen Schatten sind das gut fuenfzig Zentimeter — fuer die
 * Frage, ob ein Baum ein Dach verschattet, ohne Belang.
 */
function alsRaster(fenster: Hoehenfenster, bezug: Geo): Hoehenraster {
  const werte = new Float32Array(fenster.werte.length);
  for (let i = 0; i < werte.length; i++) {
    const wert = fenster.werte[i];
    werte[i] = wert === null || wert === undefined ? Number.NaN : wert;
  }
  return {
    werte,
    spalten: fenster.spalten,
    zeilen: fenster.zeilen,
    ursprung: nachGrundriss(fenster.ursprung, bezug),
    zellgroesseM: fenster.zellgroesseM,
  };
}

export interface Gelaendevorschlag {
  objekte: Array<GefundenesObjekt & { grundflaecheGeo: Geo[] }>;
  meldung: string;
}

/**
 * Objekte aus den Gelaendedaten vorschlagen.
 *
 * Was herauskommt, sind ganz normale Verschattungsobjekte — dieselben, die man
 * sonst von Hand setzt. Sie lassen sich danach aendern und loeschen wie alle
 * anderen auch.
 */
export async function objekteAusGelaende(
  projektId: string,
  standort: Geo,
  optionen: { mindesthoeheM?: number; hoechstens?: number } = {},
): Promise<Gelaendevorschlag> {
  const daten = await db.hoehendaten.get(projektId);
  if (!daten) {
    return {
      objekte: [],
      meldung: 'Noch keine Geländedaten geladen. In der Projektliste "Vorbereiten".',
    };
  }
  if (!daten.dgm || !daten.dom) {
    return {
      objekte: [],
      meldung: 'Es fehlt eines der beiden Modelle. Aus DOM minus DGM entstehen die Objekte.',
    };
  }

  const bezug = bezugspunkt([standort]);
  const ndom = objekthoehen(alsRaster(daten.dom, bezug), alsRaster(daten.dgm, bezug));
  const gefunden = findeVerschattungsobjekte(ndom, {
    mindesthoeheM: optionen.mindesthoeheM ?? 3,
    hoechstens: optionen.hoechstens ?? 25,
  });

  return {
    objekte: gefunden.map((objekt) => ({
      ...objekt,
      grundflaecheGeo: objekt.grundflaeche.map((p) => nachGeo(p, bezug)),
    })),
    meldung:
      gefunden.length === 0
        ? 'Im Umkreis steht nichts, was hoch genug wäre. Verschattungsobjekte bitte von Hand setzen.'
        : `${gefunden.length} Objekt(e) gefunden. Art und Höhe sind geschätzt — bitte nachsehen.`,
  };
}
